import {useDisclosure} from '@chakra-ui/react';
import {useRestaurantDistancePrice} from 'api/queries';
import {useToast, useTranslation} from 'hooks';
import cloneDeep from 'lodash.clonedeep';
import {useRouter} from 'next/router';
import {FC, PropsWithChildren, useEffect, useMemo, useState} from 'react';
import GTMService from 'services/ga4';
import {useCartStore} from 'store/cartStore';
import {ICartItem, IRestaurant} from 'types';
import {calculateProductOptionsPrice, isSameProduct} from 'utils/cartUtils';
import {useShallow} from 'zustand/react/shallow';

import {useWasPreviousMetroRoute} from '../../hooks/useWasPreviousMetroRoute';
import {CartContext} from './contexts/CartContext';
import {useCustomerContext} from './contexts/CustomerContext';
import {useOrderContext} from './contexts/OrderContext';

// Re-export the hook for backward compatibility
export {useCartContext} from './contexts/CartContext';
export type {ICartContext} from './contexts/CartContext';

const CartProvider: FC<PropsWithChildren> = ({children}) => {
  const router = useRouter();
  const isMetroRoute =
    router.pathname.startsWith('/categories/') ||
    router.pathname.startsWith('/metro') ||
    router.pathname.startsWith('/market-category') ||
    router.pathname.startsWith('/favorite-product');
  const isCheckoutRoute = router.pathname.startsWith('/checkout');
  const isCartRoute = router.pathname.startsWith('/cart');

  // https://docs.pmnd.rs/zustand/guides/prevent-rerenders-with-use-shallow
  const {cartItems, timestamp, provider, metro_cartItems, metro_timestamp, metro_provider} = useCartStore(
    useShallow((state) => state),
  );

  const {
    clearAll,
    updateTimestamp,
    setCartItems,
    setProvider,
    changeCartItemQuantity,
    deleteCartItemById,
    colaPromo,
    // Metro actions
    clearMetroAll,
    updateMetroTimestamp,
    setMetroCartItems,
    setMetroProvider,
    changeMetroCartItemQuantity,
    deleteMetroCartItemById,
    metro_colaPromo,
  } = useCartStore();

  const isFromMetro = useWasPreviousMetroRoute();

  /* Determine which cart to use based on route */
  const shouldUseMetroCart = useMemo(() => {
    if (isCheckoutRoute || isCartRoute) {
      return isFromMetro;
    }

    return isMetroRoute;
  }, [isFromMetro, isCheckoutRoute, isCartRoute, isMetroRoute]);

  const shouldAddToMetroCart = isMetroRoute;

  const currentCartItems = shouldUseMetroCart ? metro_cartItems : cartItems;
  const currentTimestamp = shouldUseMetroCart ? metro_timestamp : timestamp;
  const currentProvider = shouldUseMetroCart ? metro_provider : provider;
  const currentColaPromo = shouldUseMetroCart ? metro_colaPromo : colaPromo;

  const [deliveryDetails, setDeliveryDetails] = useState({
    remainingAmount: 0,
    deliveryAmount: 0,
  });
  const toast = useToast();
  const {t} = useTranslation();
  const {voucher, refetchActiveOrders, resetState} = useOrderContext();
  const {customer} = useCustomerContext();

  const {isOpen: sideCartOpen, onOpen, onClose} = useDisclosure();

  const hasDeliveryPrice = useMemo(() => Array.isArray(currentProvider?.delivery_price), [currentProvider]);

  const FREE_DELIVERY_START_PRICE =
    hasDeliveryPrice && currentProvider?.delivery_price && currentProvider.delivery_price.length > 0
      ? currentProvider.delivery_price[currentProvider.delivery_price.length - 1]?.price_from ?? 0
      : 0;

  const requiredProducts = useMemo(() => currentProvider?.required_products ?? [], [currentProvider]);

  const {
    data: dynamicDeliveryPrice = {
      surcharge: 0,
      surcharge_for_out_area: 0,
      distance_to_client: 0,
    },
  } = useRestaurantDistancePrice({
    providerSlug: currentProvider?.slug,
  });

  const addProvider = (provider: IRestaurant) => {
    if (shouldAddToMetroCart) {
      setMetroProvider(provider);
      updateMetroTimestamp();
    } else {
      setProvider(provider);
      updateTimestamp();
    }
  };

  const editCartItem = (cartItem: ICartItem) => {
    if (shouldAddToMetroCart) {
      setMetroCartItems(
        metro_cartItems.map((item) => {
          if (item.id === cartItem.id) return cartItem;
          return item;
        }),
      );
    } else {
      setCartItems(
        cartItems.map((item) => {
          if (item.id === cartItem.id) return cartItem;
          return item;
        }),
      );
    }
  };

  const addProductToCart = (cartItem: ICartItem, crossSelling?: ICartItem[]) => {
    const routeCartItems = shouldAddToMetroCart ? metro_cartItems : cartItems;
    const alreadyAdded = routeCartItems.find((item) => isSameProduct(item, cartItem));

    if (alreadyAdded) {
      if (shouldAddToMetroCart) {
        return changeMetroCartItemQuantity(alreadyAdded.id, alreadyAdded.quantity + cartItem.quantity);
      } else {
        return changeCartItemQuantity(alreadyAdded.id, alreadyAdded.quantity + cartItem.quantity);
      }
    }

    let newCartItems = [...routeCartItems, cartItem];
    if (crossSelling) newCartItems = newCartItems.concat(crossSelling);

    if (shouldAddToMetroCart) {
      setMetroCartItems(newCartItems);
    } else {
      setCartItems(newCartItems);
    }
    GTMService.addToCart([cartItem], currentProvider?.slug ?? '');
    toast.success(t('ADDED'));
  };

  const clearCart = (withoutNotification?: boolean) => {
    if (shouldUseMetroCart) {
      clearMetroAll(true);
    } else {
      clearAll(true);
    }
    resetState();
    if (!withoutNotification) toast.success(t('CART_EMPTY'));
  };

  const deleteProduct = (cartItemId: string) => {
    const itemsAfterDeletion = shouldUseMetroCart
      ? deleteMetroCartItemById(cartItemId)
      : deleteCartItemById(cartItemId);
    !!itemsAfterDeletion.length && toast.success(t('DELETED'));

    if (!itemsAfterDeletion.length) clearCart(true);
  };

  const resetDeliveryPrice = () => {
    if (currentProvider?.delivery_price?.length) {
      let providerDeliveryPrice = currentProvider.delivery_price[0];
      for (let i = 0; i < currentProvider.delivery_price.length; i++) {
        const item = currentProvider.delivery_price[i];
        if (!item) continue;
        if (cartItemsPrice >= item.price_from) {
          providerDeliveryPrice = item;
        }
      }
      setDeliveryDetails({
        deliveryAmount: providerDeliveryPrice.price,
        remainingAmount: providerDeliveryPrice.price_from - cartItemsPrice,
      });
    } else {
      setDeliveryDetails({
        deliveryAmount: 0,
        remainingAmount: 0,
      });
    }
  };

  const cartItemsPrice = useMemo(() => {
    const basePrice = currentCartItems.reduce((priceSum, cartItem) => {
      const {optionsPrice, discountOptions} = calculateProductOptionsPrice(cartItem, true);

      if (discountOptions) return priceSum + discountOptions;
      return priceSum + cartItem.product.discount_price * cartItem.quantity + optionsPrice;
    }, 0);

    const requiredProductsPrice = requiredProducts.reduce((priceSum, item) => {
      return priceSum + item.price;
    }, 0);
    return basePrice + requiredProductsPrice;
  }, [currentCartItems, requiredProducts]);

  const restToFreeDelivery = FREE_DELIVERY_START_PRICE - cartItemsPrice;

  const [isColaFreeDelivery, setIsColaFreeDelivery] = useState(false);

  useEffect(() => {
    if (currentProvider) {
      let providerDeliveryPrice = hasDeliveryPrice ? currentProvider.delivery_price[0] : null;
      if (!providerDeliveryPrice) return;

      if (voucher?.is_free_delivery) {
        setDeliveryDetails({remainingAmount: 0, deliveryAmount: 0});
        return;
      }
      // First Free Delivery
      if (customer.free_delivery && customer?.minimal_order_sum <= cartItemsPrice) {
        console.log('>>>freedeliver', deliveryDetails);

        return setDeliveryDetails({deliveryAmount: 0, remainingAmount: 0});
      }

      for (let i = 0; i < currentProvider.delivery_price.length; i++) {
        const item = currentProvider.delivery_price[i];
        if (!item) continue;
        if (cartItemsPrice >= item.price_from) {
          providerDeliveryPrice = item;
          //break;
        }
      }
      const totalRemainingAmount = providerDeliveryPrice.price_from - cartItemsPrice;

      setDeliveryDetails({
        remainingAmount: totalRemainingAmount,
        deliveryAmount: providerDeliveryPrice.price,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cartItemsPrice,
    voucher?.is_free_delivery,
    currentProvider,
    hasDeliveryPrice,
    customer.free_delivery,
    customer?.minimal_order_sum,
  ]);

  useEffect(() => {
    //Cola
    if (currentColaPromo?.cola_promo_active) {
      const colaItemsCount = currentCartItems.reduce(
        (count, item) => count + (item.product.is_cola ? item.quantity : 0),
        0,
      );
      const isEligible = colaItemsCount >= 2 && cartItemsPrice >= currentColaPromo.cola_promo_threshold;
      setIsColaFreeDelivery(colaItemsCount >= 2);
      if (isEligible) return setDeliveryDetails({remainingAmount: 0, deliveryAmount: 0});
    }

    if (voucher && !voucher.is_free_delivery && deliveryDetails.deliveryAmount === 0) {
      const isFifthOrderFreeDelivery =
        customer.month_order_count >= customer.each_n_free - 1 &&
        (customer?.minimal_order_sum <= cartItemsPrice || customer?.minimal_order_sum == 0);

      if (!isFifthOrderFreeDelivery) return;

      if (currentProvider?.delivery_price?.length) {
        let providerDeliveryPrice = currentProvider.delivery_price[0];
        for (let i = 0; i < currentProvider.delivery_price.length; i++) {
          const item = currentProvider.delivery_price[i];
          if (!item) continue;
          if (cartItemsPrice >= item.price_from) {
            providerDeliveryPrice = item;
          }
        }
        setDeliveryDetails({
          deliveryAmount: providerDeliveryPrice.price,
          remainingAmount: providerDeliveryPrice.price_from - cartItemsPrice,
        });
      }
    }

    if (
      (!voucher || voucher.is_free_delivery) &&
      customer.month_order_count >= customer.each_n_free - 1 &&
      (customer?.minimal_order_sum <= cartItemsPrice || customer?.minimal_order_sum == 0)
    ) {
      setDeliveryDetails({deliveryAmount: 0, remainingAmount: 0});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cartItemsPrice,
    customer.each_n_free,
    customer?.minimal_order_sum,
    customer?.month_order_count,
    restToFreeDelivery,
    currentCartItems,
    currentColaPromo?.cola_promo_active,
    currentColaPromo?.cola_promo_threshold,
    voucher,
  ]);

  useEffect(() => {
    if (currentCartItems.length === 0) onClose();
  }, [currentCartItems, onClose]);

  return (
    <CartContext.Provider
      value={{
        cartItems: cloneDeep(currentCartItems), //Cannot add property 5813, object is not extensible
        requiredProducts,
        dynamicDeliveryPrice:
          deliveryDetails.deliveryAmount == 0
            ? {
                surcharge: 0,
                surcharge_for_out_area: 0,
                distance_to_client: 0,
              }
            : dynamicDeliveryPrice,
        cartItemsPrice,
        deliveryDetails: {
          ...deliveryDetails,
          deliveryAmount: deliveryDetails.deliveryAmount,
        },
        restToFreeDelivery,
        cartProvider: currentProvider,
        timestamp: currentTimestamp || new Date(),
        updateTimestamp: shouldUseMetroCart ? updateMetroTimestamp : updateTimestamp,
        refetchActiveOrders,
        addProvider,
        addProductToCart,
        editCartItem,
        changeCartItemQuantity: shouldUseMetroCart ? changeMetroCartItemQuantity : changeCartItemQuantity,
        clearCart,
        deleteProduct,
        setCartItems: shouldUseMetroCart ? setMetroCartItems : setCartItems,
        sideCartOpen,
        setSideCartOpen: (openSide: boolean) => {
          if (openSide) onOpen();
          else onClose();
        },
        colaPromo: currentColaPromo,
        isColaFreeDelivery,
        resetDeliveryPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export {CartProvider};
